import { RouteComponentProps, useNavigate } from '@reach/router';
import { AppPath, PUBLIC_SITE_URL } from 'appConstants';
import { ChoosePackageForm } from 'components/choosePackageForm/ChoosePackageForm';
import ConfirmationBox from 'components/confirmationBox/ConfirmationBox';
import FormHeader from 'components/formHeader/FormHeader';
import PaymentDetailsForm from 'components/paymentDetailsForm/PaymentDetailsForm';
import { PaymentTypeForm } from 'components/paymentTypeForm/PaymentTypeForm';
import ProfileDetailsForm from 'components/profileDetailsForm/ProfileDetailsForm';
import SchoolDetailsForm from 'components/schoolDetailsForm/SchoolDetailsForm';
import Page from 'components/shared/Page';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  clearPackageForm,
  clearPoNumber,
  clearSchoolDetails,
  setRegistrationToken,
  setValidPromoCode,
  selectMembershipOptInfo,
} from 'reducers/misc';
import { SubscribeParser, SubscriptionStep } from './models';
import { Spinner } from 'components/spinner/Spinner';
import { getWithParams } from 'helpers/endpointCalls';
import { endpoints } from 'endpoints.config';
import WarningIcon from '../../images/Icon_Warning.png';
import { setMembershipOptInfo } from 'reducers/misc';
import { setQuotesId, setQuotePromoCodeSlots } from 'reducers/misc';
import {
  durationOptionsFromQuote,
  isQuoteUnavailable,
  quoteHasExpired,
  quotePackagesFromQuote,
} from './quoteOptions';
import { slotsFromQuotePackages } from 'subscriptionOptions/promoCodeSlots';


const SUCCESS_MESSAGE = {
  title: 'Congratulations',
  subTitle: 'Woohoo! Welcome to the Get Set family.',
  message: 'Click below to access your content.',
  buttonText: "Let's begin",
};

const INVOICE_SUCCESS_MESSAGE = {
  title: 'Congratulations',
  subTitle: 'Woohoo! Welcome to the Get Set family.',
  message: 'Click below to access your content.',
  buttonText: "Let's begin",
};

export const Subscribe: React.FC<RouteComponentProps> = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [subscriptionStep, setSubscriptionStep] = useState<SubscriptionStep>(
    SubscriptionStep.CHOOSE_PACKAGE
  );
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);
  const [subscriptionIsComplete, setSubscriptionIsComplete] = useState(false);

  const selectedPackageDetails = useSelector(selectMembershipOptInfo);

  useEffect(() => {
    return () => {
      dispatch(clearSchoolDetails());
      dispatch(setRegistrationToken(''));
      dispatch(clearPackageForm());
      dispatch(setValidPromoCode([]));
      dispatch(setQuotePromoCodeSlots({}));
      dispatch(clearPoNumber());
    };
  }, []);

  useEffect(() => {
    const quoteId = new URLSearchParams(window.location.search).get('id');

    if(quoteId){
      loadQuoteData(parseInt(quoteId));
    } else {
      setLoading(false);
    }
  }, []); 
  
  const showQuoteExpired = () => {
    navigate("/membership-options", {
      state: {
        quoteExpired: true,
      },
    });
  };

  const loadQuoteData = async (quoteId: number) => {
    const result = await getWithParams(endpoints.public.subscription.getQuoteStatus, {
      Id: quoteId
    });

    if (result.response && !result.errors) {
      dispatch(setQuotesId(quoteId));

      if(quoteHasExpired(result.response, new Date())){
        showQuoteExpired();
      } else {
        // The quote prices one order per duration, so the options on offer are the orders it
        // priced. The two-slot shape below is what the later steps still read.
        const durationOptions = durationOptionsFromQuote(result.response);
        const quotePackages = quotePackagesFromQuote(result.response);

        // Matched on duration, not position: the later steps match a selection by the option's
        // label, so a quote that priced only the longer order must leave the one-year slot empty
        // rather than fill it with an option labelled "3 Years".
        const oneYearPackageObj = durationOptions.find((option) => option.subscriptionDuration === 1);
        const threeYearPackageObj = durationOptions.find((option) => option.subscriptionDuration === 3);

        // The quote priced its own codes per package, so which subject holds which code is the
        // server's answer and needs no re-check.
        dispatch(setQuotePromoCodeSlots(slotsFromQuotePackages(quotePackages)));

        dispatch(setMembershipOptInfo([{
          selectedLocation: result.response.locationTypesId,
          selectedLocationLabel: result.response.locationName,
          selectedPackage: result.response.generalPackagesId,
          selectedSubject: result.response.subjectsIds,
          selectedSubjectName: result.response.subjectName,
          ageGroup: result.response.ageGroup,
          pupilsOnRoll: result.response.pupilsOnRoll,
          promoCodes: [result.response.promoCode, result.response.promoCode2].filter((code: string) => !!code),
          oneYearPackage: oneYearPackageObj,
          threeYearPackage: threeYearPackageObj,
          /** Every package the quote holds, so a promo code resolves to its subject. */
          quotePackages: quotePackages,
          vatCountry: result.response.countriesId,
          vatCode: result.response.vatNumber || null
        }]));
        setLoading(false);
      }
    } else if (isQuoteUnavailable(result.errors)) {
      showQuoteExpired();
    } else {
      setLoading(false);

      setTimeout(function(){
        setError(true);
      }, 500);
    }
  };

  if(!loading && error){
    return (
      <Page isPublic className={'SubscribePage'}>
        <div className="Content WidthContent HeightContent">
          <div className="InfoTableContainer">
            <div className="FailedMsg">
              <div className="MsgContainer Error">
                <div className="Img">
                  <img src={WarningIcon} alt="Warning" />
                </div>
                <div className="Txt">An error occurred.</div>
                <div className="LineSpacer4"></div>
                <div className="Btn BtnOrange" onClick={() => navigate("/membership-options")}>Membership Options</div>
              </div>
            </div>
          </div>
        </div>
      </Page>
    );
  }

  if(!loading && !selectedPackageDetails) {
    window.location.href = AppPath.MEMBERSHIP_OPTIONS;
    return null;
  } 

  return (
    <Page isPublic className={'SubscribePage'}>
      {loading ?
          <div
            style={{
              minHeight: ' calc(100vh - 300px)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Spinner color={'#f59e16'} />
          </div>      
        :
        <>
          {subscriptionIsComplete ? (
            <div className="Success">
              <div className="ConfirmationBoxContainer">
                <ConfirmationBox
                  {...SUCCESS_MESSAGE}
                  buttonOnClick={() => navigate(AppPath.SIGN_IN)}
                />
              </div>
            </div>
          ) : (
            <div className="Content WidthContent HeightContent">
              <div className="SubscriptionPageHead">
                <FormHeader
                  activeStep={subscriptionStep < 5 ? subscriptionStep + 1 : 5}
                  numOfSteps={5}
                  title={SubscribeParser.getPageHeaderInfo(subscriptionStep, selectedPackageDetails).title}
                  nextStep={
                    SubscribeParser.getPageHeaderInfo(subscriptionStep, selectedPackageDetails).nextStep
                  }
                />
              </div>
              {subscriptionStep === SubscriptionStep.CHOOSE_PACKAGE ? (
                <ChoosePackageForm
                  onSubmit={() =>
                    setSubscriptionStep(SubscriptionStep.SCHOOL_DETAILS)
                  }
                />
              ) : subscriptionStep === SubscriptionStep.SCHOOL_DETAILS ? (
                <SchoolDetailsForm
                  onSubmit={() => {
                    setSubscriptionStep(SubscriptionStep.PROFILE_DETAILS)
                  }
                  }
                />
              ) : subscriptionStep === SubscriptionStep.PROFILE_DETAILS ? (
                <ProfileDetailsForm
                  onSubmit={() =>
                    setSubscriptionStep(SubscriptionStep.PAYMENT_TYPE)
                  }
                />
              ) : subscriptionStep === SubscriptionStep.PAYMENT_TYPE ? (
                <PaymentTypeForm
                  onSubmit={(typeOfPayment: 'card' | 'invoice') => {
                    if (typeOfPayment === 'card') {
                      setSubscriptionStep(SubscriptionStep.PAYMENT_DETAILS);
                    } else {
                      setSubscriptionStep(SubscriptionStep.CONFIRMATION);
                    }
                  }}
                />
              ) : subscriptionStep === SubscriptionStep.PAYMENT_DETAILS ? (
                <PaymentDetailsForm
                  onSubmit={() => {
                    setSubscriptionIsComplete(true);
                  }}
                />
              ) : subscriptionStep === SubscriptionStep.CONFIRMATION ? (
                <div className="Success">
                  <div className="ConfirmationBoxContainer">
                    <ConfirmationBox
                      {...INVOICE_SUCCESS_MESSAGE}
                      buttonOnClick={() => navigate(AppPath.SIGN_IN)}
                    />
                  </div>
                </div>
              ) : null}
            </div>
          )}
        </>
      }
    </Page>
  );
};
